import { useEffect, useState } from "react";
import dayjs from "dayjs";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Card } from "../../UI/Card.tsx";
import { LoadingBlock } from "../../UI/Loading/LoadingRows.tsx";
import {
  HistoryPeriodsDTO,
  useGetHubSpotCloserAvailabilityHistory,
} from "../../HubSpot/useGetHubSpotCloserAvailabilityHistory.ts";
import { formatRate } from "../Stats/closerStatsFormat.ts";
import { StatsRange } from "../Stats/statsPeriod.ts";

type WeekAvailability = {
  label: string;
  effective: number | null;
  inCall: number | null;
  fill: number | null;
  lostAfterOpening: number | null;
};

function weeksOf(
  history: HistoryPeriodsDTO,
  email: string,
): WeekAvailability[] {
  const target = email.toLowerCase();
  return history.periods.map((period) => {
    const closer = period.closers.find((c) => c.email.toLowerCase() === target);
    const closure = closer?.closure ?? null;
    const opening = closer?.opening ?? null;
    return {
      label: dayjs(period.periodStart).format("DD/MM"),
      effective: closure?.effective ?? null,
      inCall: closure?.inCall ?? null,
      fill:
        closure && closure.effective > 0
          ? Math.round((closure.inCall / closure.effective) * 1000) / 10
          : null,
      lostAfterOpening:
        closure && opening
          ? Math.max(0, opening.effective - closure.effective)
          : null,
    };
  });
}

function sum(values: (number | null)[]): number {
  return values.reduce<number>((total, v) => total + (v ?? 0), 0);
}

export function CloserAvailabilityTrendCard({
  email,
  range,
}: {
  email: string;
  range: StatsRange;
}) {
  const { getHistoryPeriods } = useGetHubSpotCloserAvailabilityHistory();
  const [weeks, setWeeks] = useState<WeekAvailability[] | null>(null);
  const [error, setError] = useState(false);
  const { fromDay, toDay } = range;

  useEffect(() => {
    let cancelled = false;
    setWeeks(null);
    setError(false);
    getHistoryPeriods("week", { from: fromDay, to: toDay })
      .then((history) => {
        if (!cancelled) setWeeks(weeksOf(history, email));
      })
      .catch((err) => {
        console.error("Error fetching availability history:", err);
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [getHistoryPeriods, email, fromDay, toDay]);

  const effective = sum(weeks?.map((w) => w.effective) ?? []);
  const inCall = sum(weeks?.map((w) => w.inCall) ?? []);
  const lost = sum(weeks?.map((w) => w.lostAfterOpening) ?? []);

  return (
    <Card>
      <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-1">
        Disponibilità per settimana
      </h2>
      <p className="text-xs text-slate-400 mb-3">
        Dallo storico dei calendari, alla chiusura di ogni settimana. Slot
        persi: slot effettivi all'apertura che a fine settimana non c'erano più.
      </p>
      {error && (
        <p className="text-sm text-red-600">
          Errore nel recupero dello storico delle disponibilità.
        </p>
      )}
      {!error && !weeks && <LoadingBlock sizeClassName="h-64 w-full" />}
      {weeks && (
        <>
          <div className="flex flex-wrap gap-6 text-sm mb-2">
            <span>
              Slot effettivi:{" "}
              <span className="font-semibold tabular-nums">{effective}</span>
            </span>
            <span>
              In call:{" "}
              <span className="font-semibold tabular-nums">{inCall}</span>
            </span>
            <span>
              Riempimento:{" "}
              <span className="font-semibold tabular-nums">
                {formatRate(effective === 0 ? null : inCall / effective)}
              </span>
            </span>
            <span>
              Slot persi dopo l'apertura:{" "}
              <span className="font-semibold tabular-nums">{lost}</span>
            </span>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <ComposedChart
              data={weeks}
              margin={{ top: 16, right: 8, left: 0, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              <YAxis yAxisId="slots" tick={{ fontSize: 12 }} />
              <YAxis
                yAxisId="pct"
                orientation="right"
                tick={{ fontSize: 12 }}
                tickFormatter={(v: number) => `${v}%`}
              />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar
                yAxisId="slots"
                dataKey="effective"
                name="Slot effettivi"
                fill="#C7D5F5"
                radius={[3, 3, 0, 0]}
              />
              <Bar
                yAxisId="slots"
                dataKey="inCall"
                name="In call"
                fill="#1E429F"
                radius={[3, 3, 0, 0]}
              />
              <Bar
                yAxisId="slots"
                dataKey="lostAfterOpening"
                name="Slot persi"
                fill="#F97316"
                radius={[3, 3, 0, 0]}
              />
              <Line
                yAxisId="pct"
                dataKey="fill"
                name="Riempimento %"
                stroke="#047857"
                strokeWidth={2}
                dot={{ r: 3 }}
                connectNulls={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </>
      )}
    </Card>
  );
}
